'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

type Step = 'email' | 'otp' | 'success';

const DEMO_ACCOUNTS = [
  { email: 'sarah.chen@apc.law', name: 'Sarah Chen', role: 'Super Admin', domain: 'admin' as const, adminRole: 'super-admin' as const },
  { email: 'marcus.webb@apc.law', name: 'Marcus Webb', role: 'APC Lawyer', domain: 'admin' as const, adminRole: 'apc-lawyer' as const },
  { email: 'alex@northstarlabs.io', name: 'Alex Morgan', role: 'Primary Founder', domain: 'founder' as const, founderRole: 'primary-founder' as const },
  { email: 'priya@northstarlabs.io', name: 'Priya Shah', role: 'Co-founder', domain: 'founder' as const, founderRole: 'co-founder' as const },
];

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [selectedDemo, setSelectedDemo] = useState<typeof DEMO_ACCOUNTS[0] | null>(null);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const t = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCountdown]);

  function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) { setError('Please enter a valid email address.'); return; }

    const match = DEMO_ACCOUNTS.find((a) => a.email.toLowerCase() === email.trim().toLowerCase());
    setSelectedDemo(match ?? null);

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep('otp');
      setResendCountdown(30);
    }, 800);
  }

  function handleOtpChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setError('');
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  function handleOtpPaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  }

  function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 6) { setError('Please enter the full 6-digit code.'); return; }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      // Accept any 6-digit code for demo; in production this hits the backend
      if (code === '000000') {
        setError('Invalid code. Use any 6-digit code to sign in (demo mode).');
        return;
      }

      // Set the user in localStorage based on matched account
      if (selectedDemo) {
        const user = {
          userId: selectedDemo.email,
          name: selectedDemo.name,
          email: selectedDemo.email,
          domain: selectedDemo.domain,
          ...(selectedDemo.domain === 'admin' ? { adminRole: selectedDemo.adminRole } : { founderRole: (selectedDemo as any).founderRole }),
        };
        if (selectedDemo.domain === 'admin') {
          localStorage.setItem('apc_current_admin_user', JSON.stringify(user));
        } else {
          localStorage.setItem('apc_current_founder_user', JSON.stringify(user));
        }
        window.dispatchEvent(new Event('apc-role-change'));
      }

      setStep('success');
      setTimeout(() => {
        if (selectedDemo?.domain === 'admin') {
          router.push('/admin');
        } else {
          router.push('/dashboard');
        }
      }, 1200);
    }, 900);
  }

  function handleResend() {
    if (resendCountdown > 0) return;
    setOtp(['', '', '', '', '', '']);
    setError('');
    setResendCountdown(30);
    otpRefs.current[0]?.focus();
  }

  return (
    <div className="min-h-screen bg-apc-ink flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] shrink-0 bg-apc-deep-teal border-r border-white/8 p-10 relative overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-apc-teal/10 blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-apc-copper/8 blur-3xl translate-y-1/3 -translate-x-1/3" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-9 h-9 rounded-lg bg-apc-teal flex items-center justify-center">
              <span className="text-sm font-bold text-white">A</span>
            </div>
            <span className="text-base font-semibold text-white">APC Foundations</span>
          </div>

          <div className="space-y-6">
            <div>
              <p className="section-label text-apc-aqua mb-3">Legal readiness platform</p>
              <h1 className="font-display text-3xl xl:text-4xl text-white leading-tight">
                Built for founders who move fast and need to stay protected.
              </h1>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              APC Foundations gives early-stage startups the legal infrastructure to raise, hire, and scale — without the traditional law firm overhead.
            </p>
          </div>
        </div>

        {/* Feature list */}
        <div className="relative z-10 space-y-3">
          {[
            { icon: 'ShieldCheckIcon', text: 'Legal health scoring across 8 risk categories' },
            { icon: 'DocumentTextIcon', text: 'Automated document generation & review' },
            { icon: 'UserGroupIcon', text: 'Role-based access for your entire team' },
          ].map((f) => (
            <div key={f.text} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-md bg-apc-teal/30 flex items-center justify-center shrink-0 mt-0.5">
                <Icon name={f.icon as any} size={14} className="text-apc-aqua" />
              </div>
              <p className="text-sm text-white/70 leading-snug">{f.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[400px]">

          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-10 lg:hidden">
            <div className="w-8 h-8 rounded-lg bg-apc-teal flex items-center justify-center">
              <span className="text-xs font-bold text-white">A</span>
            </div>
            <span className="text-sm font-semibold text-white">APC Foundations</span>
          </div>

          {step === 'email' && (
            <div className="fade-in">
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-2">Sign in</h2>
                <p className="text-sm text-white/50">Enter your email to receive a one-time code.</p>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">Email address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="you@example.com"
                    autoFocus
                    className="w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-apc-aqua/60 focus:bg-white/8 transition-all"
                  />
                  {error && (
                    <p className="mt-1.5 text-xs text-status-red flex items-center gap-1.5">
                      <Icon name="ExclamationCircleIcon" size={13} />
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-apc-teal hover:bg-apc-teal/90 disabled:opacity-60 text-white font-medium text-sm py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending code…
                    </>
                  ) : (
                    <>
                      Continue
                      <Icon name="ArrowRightIcon" size={15} />
                    </>
                  )}
                </button>
              </form>

              {/* Demo accounts */}
              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-white/8" />
                  <span className="text-xs text-white/30 font-medium">Demo accounts</span>
                  <div className="flex-1 h-px bg-white/8" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {DEMO_ACCOUNTS.map((acc) => (
                    <button
                      key={acc.email}
                      type="button"
                      onClick={() => setEmail(acc.email)}
                      className="text-left px-3 py-2.5 rounded-lg bg-white/4 hover:bg-white/8 border border-white/8 hover:border-white/15 transition-all group"
                    >
                      <p className="text-xs font-medium text-white/80 group-hover:text-white truncate">{acc.name}</p>
                      <p className="text-xs text-white/35 truncate">{acc.role}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'otp' && (
            <div className="fade-in">
              <button
                onClick={() => { setStep('email'); setOtp(['', '', '', '', '', '']); setError(''); }}
                className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 mb-8 transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={13} />
                Back
              </button>

              <div className="mb-8">
                <div className="w-10 h-10 rounded-xl bg-apc-teal/20 border border-apc-teal/30 flex items-center justify-center mb-4">
                  <Icon name="EnvelopeIcon" size={18} className="text-apc-aqua" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-2">Check your email</h2>
                <p className="text-sm text-white/50">
                  We sent a 6-digit code to{' '}
                  <span className="text-white/80 font-medium">{email}</span>
                </p>
                {selectedDemo && (
                  <p className="mt-2 text-xs text-apc-aqua/70">
                    Demo mode — enter any 6 digits to sign in.
                  </p>
                )}
              </div>

              <form onSubmit={handleVerify} className="space-y-6">
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-3">One-time code</label>
                  <div className="flex gap-2.5" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => { otpRefs.current[i] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        autoFocus={i === 0}
                        className={`w-full aspect-square text-center text-lg font-semibold rounded-lg border transition-all focus:outline-none
                          ${digit ? 'bg-apc-teal/20 border-apc-teal/60 text-white' : 'bg-white/5 border-white/12 text-white'}
                          ${error ? 'border-status-red/60' : 'focus:border-apc-aqua/60 focus:bg-white/8'}
                        `}
                      />
                    ))}
                  </div>
                  {error && (
                    <p className="mt-2 text-xs text-status-red flex items-center gap-1.5">
                      <Icon name="ExclamationCircleIcon" size={13} />
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.join('').length < 6}
                  className="w-full bg-apc-teal hover:bg-apc-teal/90 disabled:opacity-50 text-white font-medium text-sm py-3 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verifying…
                    </>
                  ) : (
                    'Verify & sign in'
                  )}
                </button>

                <div className="text-center">
                  <p className="text-xs text-white/40">
                    Didn&apos;t receive it?{' '}
                    {resendCountdown > 0 ? (
                      <span className="text-white/30">Resend in {resendCountdown}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResend}
                        className="text-apc-aqua hover:text-apc-aqua/80 font-medium transition-colors"
                      >
                        Resend code
                      </button>
                    )}
                  </p>
                </div>
              </form>
            </div>
          )}

          {step === 'success' && (
            <div className="fade-in text-center">
              <div className="w-14 h-14 rounded-2xl bg-status-green/15 border border-status-green/30 flex items-center justify-center mx-auto mb-5">
                <Icon name="CheckIcon" size={24} className="text-status-green" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Signed in</h2>
              <p className="text-sm text-white/50">Redirecting you now…</p>
            </div>
          )}

          <p className="mt-10 text-xs text-white/20 text-center">
            © {new Date().getFullYear()} APC Law. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}